<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { glucoseunit } from '$stores/unitsStore';
  import { studyProperties, fetchStudyProperties } from '$stores/configStore';
  import { convertGlucoseValue, formatGlucoseValue } from '$utils/unitUtils';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate } from '@fortawesome/pro-light-svg-icons';
  const API_BASE_URL = '/api/proxy';
  const demoMode = import.meta.env.VITE_DEMO_MODE === 'true';

  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import SiteDetails from '$lib/SiteDetails.svelte';

  let readings = [];
  let resultCount = 0;
  // Sites the study configuration excludes from study-level reports (SC-140).
  let excludedSiteIds = [];
  // The confusing case: the user drilled into a site/subject that the study
  // config excludes — the dashboard shows readings, this report shows none.
  $: selectedSiteExcluded =
    excludedSiteIds.length > 0 &&
    ($selectedSite?.siteId != null && excludedSiteIds.includes(String($selectedSite.siteId)));
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let selectedType = 'ALL';
  let isExporting = false;
  let selectedRangeBelowAbove = 'ALL';

  // SMBG thresholds from study properties (with defaults)
  $: smbgHypoThreshold = $studyProperties?.glucoseParameters?.events?.smbgHypoThreshold ?? 75;
  $: smbgHyperThreshold = $studyProperties?.glucoseParameters?.events?.smbgHyperThreshold ?? 200;

  onMount(async () => {
    if ($selectedStudy) {
      await fetchStudyProperties($selectedStudy._id);
      fetchSMBGCompliance(1);
    }
  });

  // Subscribe to selectedStudy changes
  $: {
    if ($selectedStudy) {
      fetchStudyProperties($selectedStudy._id).then(() => {
        fetchSMBGCompliance(1);
      });
    }
  }

  async function fetchSMBGCompliance(page = 1) {
    try {
      isLoading = true;
      let url = `${API_BASE_URL}/glucoseReadings/studySMBGCompliance?page=${page}&pageSize=${limit}&studyGUID=${$selectedStudy._id}&subjectStatus=${selectedType}`;

      url += `&inRange=false`;

      switch (selectedRangeBelowAbove) {
        case 'HYPER':
          url += `&below=0&above=${smbgHyperThreshold}`;
          break;
        case 'HYPO':
          url += `&below=${smbgHypoThreshold}&above=0`;
          break;
        default: // ALL
          url += `&below=0&above=0`;
          break;
      }

      if ($selectedParticipant) {
        url += `&subjectGUID=${$selectedParticipant._id}`;
      }

      if ($selectedSite) {
        url += `&siteGUID=${$selectedSite._id}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch SMBG compliance');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      excludedSiteIds = responseData.excludedSiteIds || [];
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      readings = responseData.data;
    } catch (err) {
      console.error('Error fetching SMBG compliance:', err);
      readings = [];
    } finally {
      isLoading = false;
    }
  }

  function handleRefresh() {
    fetchSMBGCompliance(1);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchSMBGCompliance(newPage);
    }
  }

  async function fetchAllReadings() {
    try {
      let url = `${API_BASE_URL}/glucoseReadings/studySMBGCompliance?page=1&pageSize=999999&studyGUID=${$selectedStudy._id}&subjectStatus=${selectedType}`;

      url += `&inRange=false`;

      switch (selectedRangeBelowAbove) {
        case 'HYPER':
          url += `&below=0&above=${smbgHyperThreshold}`;
          break;
        case 'HYPO':
          url += `&below=${smbgHypoThreshold}&above=0`;
          break;
        default: // ALL
          url += `&below=0&above=0`;
          break;
      }

      if ($selectedParticipant) {
        url += `&subjectGUID=${$selectedParticipant._id}`;
      }

      if ($selectedSite) {
        url += `&siteGUID=${$selectedSite._id}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch all SMBG readings');
      }

      const result = await res.json();
      return result.data || [];
    } catch (err) {
      console.error('Error fetching all SMBG readings:', err);
      return [];
    }
  }

  async function handleExportExcel() {
    try {
      isExporting = true;
      const allReadings = await fetchAllReadings();

      const now = new Date();
      const dateStr = now
        .toLocaleString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
        .replace(/,/g, '')
        .replace(/\s+/g, '')
        .replace(/:/g, '')
        .replace(/(\d{2})([A-Za-z]{3})(\d{4})_(\d{2})(\d{2})/, '$2$1$3_$4$5');

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        reportType: 'SMBG_ComplianceDetails'
      });

      const formattedData = allReadings.map((item) => ({
        Protocol: demoMode ? 'DEMO-0001' : item.protocol || '',
        Site: demoMode ? 'SITE-0001' : item.siteId || '',
        Subject: item.subjectId || '',
        Status: item.subjectStatus || '',
        Visit: item.visit || '',
        Sensor: item.sensor || '',
        Type: item.type || '',
        Value: item.value ? convertGlucoseValue(item.value, $glucoseunit) : '',
        Unit: $glucoseunit,
        'Reading Time (Local)': item.read ? formatToLocalDateTime(item.read, item.timezone) : '',
        'Reading Time (UTC)': item.read ? formatToLocalDateTime(item.read, 'UTC') : '',
        Timezone: item.timezone || '',
      }));

      await exportToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      isExporting = false;
    }
  }
</script>

<!-- Add loading overlay for export -->
{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<StudyHeader />

<FeatureInfo 
  title="SMBG Compliance Report" 
  description="This page displays self-monitored blood glucose (SMBG) data for the selected participant or all participants in the study or selected site. 
  You can filter by glucose range (hypoglycemia/hyperglycemia) to identify readings of interest. Export data to Excel for further analysis. 
  Exports will contain timestamps in the participant's local timezone and UTC."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{:else if $selectedSite}
  <SiteDetails showOptionsMenu={false} />
{:else if $selectedStudy}
  <StudyDetails showOptionsMenu={false} />
{/if}

<!-- SC-140: make the configured site exclusion visible instead of silently
     showing an empty report. Prominent when the user is looking at an
     excluded site; informational otherwise. -->
{#if selectedSiteExcluded}
  <div class="mx-4 mt-2 px-3 py-2 rounded border border-amber-400 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm" role="alert">
    Site {$selectedSite.siteId} is excluded from study-level reports by the study
    configuration (test/UAT site), so its measurements are not shown here. The
    participant dashboard still shows this subject's readings.
  </div>
{:else if excludedSiteIds.length > 0}
  <div class="mx-4 mt-2 px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs">
    Note: site{excludedSiteIds.length > 1 ? 's' : ''} {excludedSiteIds.join(', ')}
    {excludedSiteIds.length > 1 ? 'are' : 'is'} excluded from this report by the study configuration.
  </div>
{/if}

<!-- Title and Filters -->
<Toolbar title="SMBG Compliance Report" itemCountTitle="Measurements" itemCount={resultCount}>
  {#if !$selectedParticipant}
    <div class="flex items-baseline gap-2">
      Status:
      <select
        bind:value={selectedType}
        on:change={() => handleRefresh()}
        class="form-select h-6 w-48 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
      >
        <option value="ALL" selected>All</option>
        <option value="COMPD">Completed</option>
        <option value="CONS">Consented</option>
        <option value="DISC">Discontinued</option>
        <option value="INIT">Initiated</option>
        <option value="SCFA">Screen Failed</option>
        <option value="SCRE">Screened</option>
        <option value="ENRO">Screened/Enrolled</option>
      </select>
    </div>
  {/if}
  <div class="flex items-baseline gap-2">
    Hypo/Hyper:
    <select
      bind:value={selectedRangeBelowAbove}
      on:change={() => handleRefresh()}
      class="form-select h-6 w-40 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
    >
      <option value="ALL">All</option>
      <option value="HYPER">Hyper (&gt;{smbgHyperThreshold})</option>
      <option value="HYPO">Hypo (&lt;{smbgHypoThreshold})</option>
    </select>
  </div>
  <div class="flex items-baseline">
    <button on:click={() => handleRefresh()} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
      preparing SMBG compliance report
    </p>
  </div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Site</th>
          <th class="px-4 py-2 text-left bold">Subject</th>
          <th class="px-4 py-2 text-left bold">Status</th>
          <th class="px-4 py-2 text-left bold">Visit</th>
          <th class="px-4 py-2 text-left bold">Sensor</th>
          <th class="px-4 py-2 text-left bold">Type</th>
          <th class="px-4 py-2 text-left bold">Value</th>
          <th class="px-4 py-2 text-left bold">Unit</th>
          <th class="px-4 py-2 text-left bold">Reading Time (Local)</th>
          <th class="px-4 py-2 text-left bold">Reading Time (UTC)</th>
          <th class="px-4 py-2 text-left bold">Timezone</th>
        </tr>
      </thead>
      <tbody>
        {#each readings as reading, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{reading.siteId ?? 'n/a'}</td>
            <td class="px-4 py-2">{reading.subjectId ?? 'n/a'}</td>
            <td class="px-4 py-2">{reading.subjectStatus ?? 'n/a'}</td>
            <td class="px-4 py-2">{reading.visit ?? 'n/a'}</td>
            <td class="px-4 py-2">{reading.sensor ?? 'n/a'}</td>
            <td class="px-4 py-2">{reading.type ?? 'n/a'}</td>
            <td class="px-4 py-2">{reading.value ? convertGlucoseValue(reading.value, $glucoseunit) : 'n/a'}</td>
            <td class="px-4 py-2">{$glucoseunit}</td>
            <td class="px-4 py-2"
              >{reading.read ? formatToLocalDateTime(reading.read, reading.timezone) : 'n/a'}</td
            >
            <td class="px-4 py-2"
              >{reading.read ? formatToLocalDateTime(reading.read, 'UTC') : 'n/a'}</td
            >
            <td class="px-4 py-2">{reading.timezone ?? 'n/a'}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<!-- Add Control Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .fixed {
    position: fixed;
  }

  .inset-0 {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .bg-opacity-50 {
    --tw-bg-opacity: 0.5;
  }

  .z-50 {
    z-index: 50;
  }

  .shadow-lg {
    --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color),
      0 4px 6px -4px var(--tw-shadow-color);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
      var(--tw-shadow);
  }
</style>
